import { flatten } from '@angular/compiler';
import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';
import {
  MitProjekt,
  MoveProjekterRequest,
  ProjektejerRequest,
  ProjektMappe,
  ProjektReference,
  PublicProjektSag,
} from 'src/app/api/models';
import { ProjekterService } from 'src/app/api/services';
import { InitiativPligtHelper } from 'src/app/core/helpers/initiativ-pligt-helper';
import { NotificationService } from 'src/app/core/notifications/notification.service';
import { TekstResourcePipe } from 'src/app/core/pipes/tekstresource.pipe';
import { SaveOperationsService } from 'src/app/core/services/operations/save/save-operations.service';
import { CustomValidators } from 'src/app/dynamic-form/models/custom-validators';
import { MinProjektMappe } from '../../pages/mine-projekter/min-projekt-mappe';

enum TekstResourceKeys {
  MineProjekter_Mappe_CreateModal_SuccessNotifikation = 'MineProjekter.Mappe.CreateModal.SuccessNotifikation',
  MineProjekter_Mappe_DeleteModal_SuccessNotifikation = 'MineProjekter.Mappe.DeleteModal.SuccessNotifikation',
  MineProjekter_Mappe_ProjektFlyttetNotifikation = 'MineProjekter.Mappe.ProjektFlyttetNotifikation',
  MineProjekter_Mappe_RenameModal_SuccessNotifikation = 'MineProjekter.Mappe.RenameModal.SuccessNotifikation',
  MineProjekter_Projekt_DeleteModal_SuccessNotifikation = 'MineProjekter.Projekt.DeleteModal.SuccessNotifikation',
  MineProjekter_Projekt_Rettighed_SetProjektEjer_SuccessNotification = 'MineProjekter.Projekt.Rettighed.SetProjektEjer.SuccessNotification',
  MineProjekter_Projekt_Rettighed_SetProjektEjer_ErrorNotification = 'MineProjekter.Projekt.Rettighed.SetProjektEjer.ErrorNotification',
  MineProjekter_Projekt_Rettighed_SetProjektEjer_RetryNotification = 'MineProjekter.Projekt.Rettighed.SetProjektEjer.RetryNotification',
}

@Injectable({
  providedIn: 'root',
})
export class MineProjekterService {
  harUlaesteBeskeder = false;
  allProjektMapper$ = new BehaviorSubject<MinProjektMappe[]>([]);
  filteredAndSortedProjektMapper$ = new BehaviorSubject<MinProjektMappe[]>(null);
  isDeleteProjektModalOpen$ = new BehaviorSubject<boolean>(false);
  isRenameMappeModalOpen$ = new BehaviorSubject<boolean>(false);
  isCreateMappeModalOpen$ = new BehaviorSubject<boolean>(false);
  isDeleteMappeModalOpen$ = new BehaviorSubject<boolean>(false);
  projektToDelete: MitProjekt;
  mappeToRename: MinProjektMappe;
  mappeToDelete: MinProjektMappe;
  changes$: Observable<string[]>;
  searchTerm$ = new BehaviorSubject<string>('');

  allProjekterCount$ = new BehaviorSubject<number>(null);
  filteredProjekterCount$ = new BehaviorSubject<number>(null);

  readonly tekstResourceKeys = TekstResourceKeys;

  formGroup: FormGroup = new FormGroup(
    {
      mappeName: new FormControl('', [
        Validators.maxLength(80),
        CustomValidators.trimRequired(),
        this.uniqueMappeNavnValidator(),
      ]),
    },
    {
      updateOn: 'change',
    }
  );

  private _changes$ = new BehaviorSubject<string[]>([]);
  private searchTermRegex = /"[^\\"]+"|\S+/g;

  constructor(
    private projekterService: ProjekterService,
    private notificationService: NotificationService,
    private tekstResourcePipe: TekstResourcePipe,
    private saveOperationsService: SaveOperationsService,
    private router: Router
  ) {
    this.changes$ = this._changes$.pipe(shareReplay());
  }

  searchTermChange(event: any) {
    this.searchTerm$.next(event.target.value);
    this.calculateFilteredProjects();
    this.router.navigate([], {
      replaceUrl: true,
      queryParams: this.searchTerm$.value ? { searchTerm: this.searchTerm$.value } : {}, // Save search term in query params
    });
  }

  uniqueMappeNavnValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const mappeNavn = control.value as string;

      if (
        !this.allProjektMapper$.value.some(
          projektMappe => projektMappe.navn.trim() === mappeNavn.trim()
        )
      ) {
        return null;
      } else {
        return { notUnique: 'notUnique' };
      }
    };
  }

  initialize() {
    this.allProjektMapper$.pipe(filter(pm => pm.length > 0)).subscribe(mapper => {
      mapper = mapper.sort((mappe1, mappe2) => {
        // "Mine projekter" should be the first entry
        if (
          mappe1.mappeReference.mappeType === 'IkkeTildelt' &&
          mappe2.mappeReference.mappeType !== 'IkkeTildelt'
        ) {
          return -1;
        }

        // "Arkiv" should be the last entry
        if (
          mappe2.mappeReference.mappeType === 'Arkiv' &&
          mappe1.mappeReference.mappeType !== 'Arkiv'
        ) {
          return -1;
        }

        // User created mapper should be alphabetically sorted
        if (
          mappe1.mappeReference.mappeType === 'BrugerOprettet' &&
          mappe2.mappeReference.mappeType === 'BrugerOprettet'
        ) {
          return mappe1.navn.toLowerCase() > mappe2.navn.toLowerCase() ? 1 : -1;
        }
      });
      this.calculateFilteredProjects();
    });
  }

  calculateFilteredProjects() {
    const searchString = this.searchTerm$.value;
    const searchTerms = this.calculateSearchTerms(searchString);

    const allProjektMapper = this.allProjektMapper$.value;

    if (searchTerms.length === 0 || !searchTerms[0]) {
      this.filteredAndSortedProjektMapper$.next(allProjektMapper);
      const allProjekterCount = this.countProjekterInMapper(allProjektMapper);
      this.allProjekterCount$.next(allProjekterCount);
      this.filteredProjekterCount$.next(allProjekterCount);
      return;
    }

    const filteredProjektMapper = allProjektMapper
      .map(pm => {
        return <MinProjektMappe>{
          ...pm,
          projekter: pm.projekter.filter(
            p => this.calculateMatchesForSearchTerms(searchTerms, p) === searchTerms.length
          ),
          expanded: true,
        };
      })
      .filter(pm => pm.loading || pm.projekter.length > 0);

    this.allProjekterCount$.next(this.countProjekterInMapper(this.allProjektMapper$.value));
    this.filteredProjekterCount$.next(this.countProjekterInMapper(filteredProjektMapper));
    this.filteredAndSortedProjektMapper$.next(filteredProjektMapper);
  }

  private calculateMatchesForSearchTerms(searchTerms: string[], projekt: MitProjekt): number {
    let matches = 0;
    for (const searchTerm of searchTerms) {
      if (
        this.lowerCaseIncludesSearchTerm(projekt.titel, searchTerm) ||
        this.lowerCaseIncludesSearchTerm(projekt.stedVisningNavn, searchTerm) ||
        this.sagerIncludesSearchTerm(projekt, projekt.sager, searchTerm)
      ) {
        matches++;
      }
    }
    return matches;
  }

  private calculateSearchTerms(searchString: string) {
    // The following regex matches words (anything escaped by whitespace) and groups defined by quotation marks
    // The following split() and join() removes excess quotation marks in matches
    // The sentence 'projekt hus "vej 8"' results in ['projekt', 'hus', '"vej 8"'] where quotes are then removed from '"vej 8"'
    return (searchString.match(this.searchTermRegex) || []).map(st =>
      st.toLowerCase().split('"').join('')
    );
  }

  private lowerCaseIncludesSearchTerm(str: string, searchTerm: string): boolean {
    return str.toLowerCase().includes(searchTerm);
  }

  sagerIncludesSearchTerm(
    projekt: MitProjekt,
    sager: PublicProjektSag[],
    searchTerm: string
  ): boolean {
    for (const sag of sager) {
      if (
        this.lowerCaseIncludesSearchTerm(sag.sagVisningsNavn, searchTerm) ||
        this.lowerCaseIncludesSearchTerm(
          sag.sagStatus.aktuelSagStatusTypeVisningNavn,
          searchTerm
        ) ||
        this.lowerCaseIncludesSearchTerm(
          InitiativPligtHelper.GetInitiativPligtText(sag, projekt.currentBrugerRettighedOnProjekt),
          searchTerm
        )
      ) {
        return true;
      }
    }
    return false;
  }

  fetchProjektMapper() {
    this.projekterService
      .getProjektMapper()
      .pipe(
        map(pm =>
          pm.map(
            x =>
              <MinProjektMappe>{
                loading: true,
                mappeReference: x.mappeReference,
                navn: this.getMappeName(x),
                projekter: [],
                expanded: this.getExpandState(x),
              }
          )
        )
      )
      .subscribe(projektMapper => {
        this.harUlaesteBeskeder = false;
        this.allProjektMapper$.next(projektMapper);
        projektMapper.forEach(pm => {
          this.projekterService.getMineProjekterForMappe(pm.mappeReference).subscribe(projekter => {
            pm.loading = false;
            pm.projekter = projekter;
            pm.harUlaesteBeskeder = projekter.some(p => p.harUlaesteBeskeder);

            this.harUlaesteBeskeder =
              this.harUlaesteBeskeder || pm.projekter.some(projekt => projekt.harUlaesteBeskeder);
            this.calculateFilteredProjects();
          });
        });
      });
  }

  private countProjekterInMapper(mapper: MinProjektMappe[]): number {
    const projekter = flatten(mapper.map(pm => pm.projekter));
    return projekter.length;
  }

  promptCreateMappe() {
    this.formGroup.controls['mappeName'].setValue('');
    this.formGroup.markAsPristine();
    this.isCreateMappeModalOpen$.next(true);
  }

  createMappe(mappeNavn: string) {
    const saveOperation = this.saveOperationsService.addOperation();
    this.projekterService
      .createMappe(mappeNavn)
      .pipe(
        catchError(err => {
          saveOperation.fail();
          return throwError(err);
        })
      )
      .subscribe(projektmappe => {
        this.allProjektMapper$.value.push(<MinProjektMappe>{
          expanded: false,
          loading: false,
          mappeReference: projektmappe.mappeReference,
          navn: projektmappe.navn,
          projekter: [],
        });
        this.allProjektMapper$.next(this.allProjektMapper$.value);
        this.isCreateMappeModalOpen$.next(false);
        saveOperation.complete();
        this.notificationService.success(
          null,
          this.tekstResourcePipe.transform(
            this.tekstResourceKeys.MineProjekter_Mappe_CreateModal_SuccessNotifikation,
            {
              mappe: mappeNavn,
            }
          )
        );
      });
  }

  promptRenameMappe(mappe: MinProjektMappe) {
    this.mappeToRename = mappe;
    this.formGroup.controls['mappeName'].setValue(mappe.navn);
    this.formGroup.markAsPristine();
    this.isRenameMappeModalOpen$.next(true);
  }

  renameMappe(mappe: MinProjektMappe, name: string) {
    const saveOperation = this.saveOperationsService.addOperation();
    this.projekterService
      .renameMappe({
        mappe: mappe.mappeReference,
        name: name,
      })
      .subscribe({
        next: () => {
          this.notificationService.success(
            null,
            this.tekstResourcePipe.transform(
              this.tekstResourceKeys.MineProjekter_Mappe_RenameModal_SuccessNotifikation,
              {
                oldName: mappe.navn,
                newName: name,
              }
            )
          );
          const projektmappe = this.allProjektMapper$.value.find(
            m => mappe.mappeReference.id === m.mappeReference.id
          );
          projektmappe.navn = name;
          this.allProjektMapper$.next(this.allProjektMapper$.value);
          this.isRenameMappeModalOpen$.next(false);
          saveOperation.complete();
        },
        error: () => {
          saveOperation.fail();
        },
      });
  }

  promptDeleteMappe(mappe: MinProjektMappe) {
    this.mappeToDelete = mappe;
    this.isDeleteMappeModalOpen$.next(true);
  }

  deleteMappe(mappe: MinProjektMappe) {
    const saveOperation = this.saveOperationsService.addOperation();
    this.projekterService.deleteMappe(mappe.mappeReference).subscribe({
      next: () => {
        this.notificationService.success(
          null,
          this.tekstResourcePipe.transform(
            this.tekstResourceKeys.MineProjekter_Mappe_DeleteModal_SuccessNotifikation,
            {
              mappe: mappe.navn,
            }
          )
        );

        if (mappe.projekter.length > 0) {
          const ikkeTildeltMappe = this.allProjektMapper$.value.find(
            m => m.mappeReference.mappeType === 'IkkeTildelt'
          );
          this.moveProjekterToMappe(ikkeTildeltMappe, mappe.projekter);
          ikkeTildeltMappe.expanded = true;
        }
        this.allProjektMapper$.next(
          this.allProjektMapper$.value.filter(m => m.mappeReference.id !== mappe.mappeReference.id)
        );
        this.isDeleteMappeModalOpen$.next(false);
        saveOperation.complete();
      },
      error: () => {
        saveOperation.fail();
      },
    });
  }

  promptDeleteProjekt(projekt: MitProjekt) {
    this.projektToDelete = projekt;
    this.isDeleteProjektModalOpen$.next(true);
  }

  deleteProjekt() {
    const projektId = this.projektToDelete.id;
    const projektTitel = this.projektToDelete.titel;
    this.addChange(projektId);
    const saveOperation = this.saveOperationsService.addOperation();
    this.isDeleteProjektModalOpen$.next(false);

    this.projekterService.slet(projektId).subscribe({
      next: () => {
        this.notificationService.success(
          null,
          this.tekstResourcePipe.transform(
            this.tekstResourceKeys.MineProjekter_Projekt_DeleteModal_SuccessNotifikation,
            {
              projekt: projektTitel,
            }
          )
        );
        this.allProjektMapper$.value.forEach(mappe => {
          mappe.projekter = mappe.projekter.filter(projekt => projekt.id !== projektId);
        });
        if (this.searchTerm$.value.length > 0) {
          this.calculateFilteredProjects();
        }
        saveOperation.complete();
      },
      error: () => {
        saveOperation.fail();
      },
      complete: () => {
        this.removeChange(projektId);
      },
    });
  }

  toggleExpandMappe(mappe: MinProjektMappe) {
    const projektmappe = this.filteredAndSortedProjektMapper$.value.find(
      m => mappe.mappeReference.id === m.mappeReference.id
    );
    projektmappe.expanded = !projektmappe.expanded;
  }

  setProjektEjer(projekt: MitProjekt) {
    this.addChange(projekt.id);
    const setProjektEjerRequest = <ProjektejerRequest>{
      projekt: <ProjektReference>{ id: projekt.id },
    };

    this.projekterService.setProjektejer(setProjektEjerRequest).subscribe({
      next: () => {
        this.notificationService.success(
          this.tekstResourcePipe.transform(
            this.tekstResourceKeys
              .MineProjekter_Projekt_Rettighed_SetProjektEjer_SuccessNotification
          )
        );
      },
      error: e => {
        if (e.status === 400) {
          this.notificationService.error(
            this.tekstResourcePipe.transform(
              this.tekstResourceKeys
                .MineProjekter_Projekt_Rettighed_SetProjektEjer_ErrorNotification
            )
          );
        } else {
          this.notificationService.warn(
            this.tekstResourcePipe.transform(
              this.tekstResourceKeys
                .MineProjekter_Projekt_Rettighed_SetProjektEjer_RetryNotification
            )
          );
        }
      },
      complete: () => {
        this.removeChange(projekt.id);
        this.fetchProjektMapper();
      },
    });
  }

  moveProjektToMappe(destinationMappe: MinProjektMappe, projekt: MitProjekt) {
    this.addChange(projekt.id);
    const moveProjekterRequest = <MoveProjekterRequest>{
      mappe: destinationMappe.mappeReference,
      projekter: [<ProjektReference>{ id: projekt.id }],
    };

    this.projekterService.moveProjekterToMappe(moveProjekterRequest).subscribe({
      next: () => {
        this.notificationService.success(
          null,
          this.tekstResourcePipe.transform(
            this.tekstResourceKeys.MineProjekter_Mappe_ProjektFlyttetNotifikation,
            {
              projektTitel: projekt.titel,
              mappeTitel: destinationMappe.navn,
            }
          )
        );

        this.moveProjekterToMappe(destinationMappe, [projekt]);

        // Expand destination mappe
        destinationMappe.expanded = true;
      },
      complete: () => {
        this.removeChange(projekt.id);
      },
    });
  }

  /** Resets state.
   * Resets filteredAndSortedProjektMapper$, to wait for projects to be loaded before conducting search
   * Does not reset allProjektMapper$, to keep a state of collapsed folders
   */
  reset() {
    this.filteredAndSortedProjektMapper$.next(null);
    this.searchTerm$.next('');
  }

  private moveProjekterToMappe(destinationMappe: MinProjektMappe, projekter: MitProjekt[]) {
    const projektMappeToAddProjektTo = this.allProjektMapper$.value.find(
      pm => pm.mappeReference.id === destinationMappe.mappeReference.id
    );

    projekter.forEach(projekt => {
      // Remove Projekt from old Mappe
      const projektMappeToRemoveProjektFrom = this.allProjektMapper$.value.find(
        pm => pm.mappeReference.id === projekt.mappeReference.id
      );
      const projektToRemoveIndex = projektMappeToRemoveProjektFrom.projekter.findIndex(
        p => p.id === projekt.id
      );
      projektMappeToRemoveProjektFrom.projekter.splice(projektToRemoveIndex, 1);

      // Add Projekt to new Mappe
      projekt.mappeReference = destinationMappe.mappeReference;
      projektMappeToAddProjektTo.projekter.push(projekt);
      projektMappeToRemoveProjektFrom.harUlaesteBeskeder = projektMappeToRemoveProjektFrom.projekter.some(
        p => p.harUlaesteBeskeder
      );
    });

    projektMappeToAddProjektTo.projekter.sort((proj1, proj2) =>
      proj1.createdAt > proj2.createdAt ? -1 : 1
    );
    projektMappeToAddProjektTo.harUlaesteBeskeder = projektMappeToAddProjektTo.projekter.some(
      p => p.harUlaesteBeskeder
    );
    this.allProjektMapper$.next(this.allProjektMapper$.value);
  }

  private getMappeName(mappe: ProjektMappe) {
    return mappe.mappeReference.mappeType === 'IkkeTildelt'
      ? 'Mine projekter'
      : mappe.mappeReference.mappeType === 'Arkiv'
      ? 'Arkiv'
      : mappe.navn;
  }

  private getExpandState(mappe: ProjektMappe) {
    const existingMappe = this.allProjektMapper$.value.find(
      m => mappe.mappeReference.id === m.mappeReference.id
    );

    if (existingMappe) {
      return existingMappe.expanded;
    } else {
      if (mappe.mappeReference.mappeType === 'IkkeTildelt') {
        return true;
      }
      return false;
    }
  }

  private addChange(id: string) {
    const newValue = this._changes$.value;
    newValue.push(id);
    this._changes$.next(newValue);
  }

  private removeChange(id: string) {
    const newValue = this._changes$.value.filter(_id => _id !== id);
    this._changes$.next(newValue);
  }
}
